import { Injectable, inject } from '@angular/core';

import { Observable, map } from 'rxjs';

import { ListingSummaryState } from '@core/models/listing.state';
import { MockDataService } from '@core/services/mock-data.service';

import { LISTING_REPOSITORY } from '../listing/listing.repository';
import { mockResponse } from '../mock-response';
import { WatchlistPriceSnapshot, WatchlistRepository } from './watchlist.repository';
import { WatchlistEntry, seedWatchlist } from './watchlist.seed';

const COLLECTION = 'watchlist';

@Injectable()
export class WatchlistMockRepository implements WatchlistRepository {
  private readonly entries = inject(MockDataService).collection<WatchlistEntry>(COLLECTION, seedWatchlist);
  private readonly listings = inject(LISTING_REPOSITORY);

  ids(shopperId: number): Observable<number[]> {
    return mockResponse(this.idsFor(shopperId));
  }

  list(shopperId: number): Observable<ListingSummaryState[]> {
    return this.listings.getByIds(this.idsFor(shopperId));
  }

  priceSnapshots(shopperId: number): Observable<WatchlistPriceSnapshot[]> {
    const snapshots = this.entries
      .list()
      .filter(e => e.shopperId === shopperId)
      .map(e => ({ listingId: e.listingId, priceAtSave: e.priceAtSave }));
    return mockResponse(snapshots);
  }

  /** Adding also snapshots the current Buy Now price for price-drop flags. */
  add(shopperId: number, listingId: number): Observable<number[]> {
    const exists = this.entries.list().some(e => e.shopperId === shopperId && e.listingId === listingId);
    if (exists) return mockResponse(this.idsFor(shopperId));

    return this.listings.getByIds([listingId]).pipe(
      map(found => {
        const listing = found[0];
        this.entries.upsert({
          id: this.entries.allocateId(),
          shopperId,
          listingId,
          priceAtSave: listing ? (listing.price ?? listing.buyNowPrice) : null
        });
        return this.idsFor(shopperId);
      })
    );
  }

  remove(shopperId: number, listingId: number): Observable<number[]> {
    const entry = this.entries.list().find(e => e.shopperId === shopperId && e.listingId === listingId);
    if (entry) this.entries.remove(entry.id);
    return mockResponse(this.idsFor(shopperId));
  }

  private idsFor(shopperId: number): number[] {
    return this.entries
      .list()
      .filter(e => e.shopperId === shopperId)
      .map(e => e.listingId);
  }
}
